import { getAuthToken, redirectToLogin } from '../../login/operations';

export class ApiClient {
    private readonly redirectOnUnauthorized: boolean;
    private static endpointToCheck = ['/agent/away', '/collect-capacity/api'];

    constructor(redirectOnUnauthorized = true) {
        this.redirectOnUnauthorized = redirectOnUnauthorized;
    }

    private async handleResponse<ResponseFromApi>(response: Response) {
        if (response.ok) {
            const contentType = response.headers.get('Content-Type');
            if (contentType?.includes('json')) {
                return (await response.json()) as Promise<ResponseFromApi>;
            }
            if (contentType?.includes('text')) {
                return (await response.text()) as unknown as Promise<ResponseFromApi>;
            }
            return response as unknown as Promise<ResponseFromApi>;
        }

        let body;
        try {
            body = await response.clone().json();
        } catch {
            body = undefined;
        }

        if (response.status === 404) {
            throw new NotFoundError(response.url, body);
        }

        if (response.status === 401) {
            if (this.redirectOnUnauthorized) {
                redirectToLogin();
                throw new UnauthorizedError('Unauthorized, redirect to login', response.url, body);
            }
            throw new UnauthorizedError('Unauthorized', response.url, body);
        }

        throw new HttpError(
            `Upstream HTTP Error : ${response.url} ${response.status} ${response.statusText}`,
            response.status,
            response.url,
            body,
        );
    }

    private shouldUseEtsToken(endpoint: string): boolean {
        return ApiClient.endpointToCheck.some((ep) => endpoint.includes(ep));
    }

    async get<ResponseFromApi>(endpoint: string) {
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + getAuthToken(this.shouldUseEtsToken(endpoint)),
            },
        });
        return this.handleResponse<ResponseFromApi>(response);
    }
    async postForm<ResponseFromApi>(endpoint: string, body: FormData) {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                // Do not set 'Content-Type': 'multipart/form-data' as the browser does it "better" with more infos, avoiding CORS errors
                Authorization: 'Bearer ' + getAuthToken(this.shouldUseEtsToken(endpoint)),
            },
            body,
        });
        return this.handleResponse<ResponseFromApi>(response);
    }

    async post<ResponseFromApi>(endpoint: string, body?: string, headers?: HeadersInit) {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + getAuthToken(this.shouldUseEtsToken(endpoint)),
                ...headers,
            },
            body,
        });
        return this.handleResponse<ResponseFromApi>(response);
    }

    async patch<ResponseFromApi>(endpoint: string, body?: string) {
        const response = await fetch(endpoint, {
            method: 'PATCH',
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + getAuthToken(this.shouldUseEtsToken(endpoint)),
            },
            body,
        });
        return this.handleResponse<ResponseFromApi>(response);
    }

    async put<ResponseFromApi>(endpoint: string, body?: string, headers?: HeadersInit) {
        const response = await fetch(endpoint, {
            method: 'PUT',
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + getAuthToken(this.shouldUseEtsToken(endpoint)),
                ...headers,
            },
            body,
        });
        return this.handleResponse<ResponseFromApi>(response);
    }

    async delete<ResponseFromApi>(endpoint: string) {
        const response = await fetch(endpoint, {
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + getAuthToken(this.shouldUseEtsToken(endpoint)),
            },
        });
        return this.handleResponse<ResponseFromApi>(response);
    }
}

/**
 * Base class for all API client errors
 */
export class ApiClientError extends Error {
    status: number;
    body?: { error: string };
    url?: string;

    constructor(message: string, status: number, url?: string, body?: { error: string }) {
        super(message);
        this.name = 'ApiClientError';
        this.status = status;
        this.body = body;
        this.url = url;
    }
}

/**
 * 404 Not Found error
 */
export class NotFoundError extends ApiClientError {
    constructor(url: string, body?: { error: string }) {
        super('Not Found', 404, url, body);
        this.name = 'ApiClientError NotFoundError';
    }
}

/**
 * 401 Unauthorized error
 */
class UnauthorizedError extends ApiClientError {
    constructor(message: string, url: string, body?: { error: string }) {
        super(message, 401, url, body);
        this.name = 'ApiClientError UnauthorizedError';
    }
}

/**
 * Generic HTTP error for other status codes
 */
export class HttpError extends ApiClientError {
    constructor(message: string, status: number, url?: string, body?: { error: string }) {
        super(message, status, url, body);
        this.name = 'ApiClientError HttpError';
    }
}
